/**
 * GLSL for the living pennant (flagCloth.ts). The vertex side ripples the cloth along its normal; the fragment side adds
 * the worn banner's sheen to the emissive (after `emissivemap_fragment`, before the lights read `diffuseColor`), reading
 * the painted cloth's own texels: GOLD (the shield rims and gold charges: red and green high, blue low), RUBY (red over
 * green and blue), PALE (bright, unsaturated: snow, moon, steel) and DARK (the night fields). One program for every
 * banner: the sheen is a branch on a uniform. Cost: a few value-noise taps on a few hundred pennant pixels.
 *
 * Time is KitClock's (performance.now seconds); `uClothMotion` 0 holds every motion at its rest pose and drops sweeps
 * and twinkles (Reduced effects); `uClothDetail` 0 (low tiers) drops the sparkle layers.
 */

export const CLOTH_VERT_HEAD = /* glsl */ `
attribute vec4 aCloth;
uniform float uClothTime;
uniform float uClothMotion;
uniform vec3 uClothWave;
varying vec2 vCloth;
// the ripple's offset (local units, along the cloth's normal) and its slope along the cloth's x
vec2 clothWave(float t, float len, float dir) {
  float k = uClothWave.z * 6.2831853, tm = uClothTime * uClothMotion;
  float ph = k * t - uClothWave.y * tm;
  float a = uClothWave.x * len, g = pow(max(t, 1e-4), 1.3);
  float s = sin(ph) + 0.35 * sin(2.3 * ph + 1.3);
  float ds = k * (cos(ph) + 0.805 * cos(2.3 * ph + 1.3));
  float dg = 1.3 * pow(max(t, 1e-4), 0.3);
  // d(offset)/dx = d(offset)/dt * dt/dx, dt/dx = dir / len
  return vec2(a * g * s, uClothWave.x * (dg * s + g * ds) * dir);
}`;

export const CLOTH_NORMAL = /* glsl */ `
  vec2 clothW = clothWave(aCloth.x, aCloth.z, aCloth.w);
  objectNormal.x -= clothW.y * objectNormal.z;          // z = f(x): the front sheet's normal (−f', 0, 1), the back's (f', 0, −1)
  objectNormal = normalize(objectNormal);`;

export const CLOTH_VERT = /* glsl */ `
  transformed.z += clothW.x;
  vCloth = aCloth.xy;`;

export const CLOTH_FRAG_HEAD = /* glsl */ `
uniform float uClothFx;
uniform float uClothTime;
uniform float uClothMotion;
uniform float uClothDetail;
varying vec2 vCloth;
float clHash(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
float clNoise(vec2 p) {
  vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(clHash(i), clHash(i + vec2(1.0, 0.0)), f.x), mix(clHash(i + vec2(0.0, 1.0)), clHash(i + vec2(1.0, 1.0)), f.x), f.y);
}
// a twinkle in a grid of cells: sparse points that flash and fade, each on its own beat
float clTwinkle(vec2 p, float density, float rate, float t) {
  vec2 c = floor(p); float h = clHash(c);
  vec2 d = fract(p) - 0.5 - (vec2(clHash(c + 7.1), clHash(c + 3.7)) - 0.5) * 0.6;
  float on = step(1.0 - density, h) * pow(max(0.0, sin(t * rate + h * 40.0)), 10.0);
  return on * smoothstep(0.36, 0.0, length(d));
}`;

export const CLOTH_FRAG = /* glsl */ `
  if (uClothFx > 0.5) {
    vec3 tx = diffuseColor.rgb;
    float t = uClothTime, mo = uClothMotion, de = uClothDetail * mo;
    float lum = dot(tx, vec3(0.2126, 0.7152, 0.0722));
    float sat = max(tx.r, max(tx.g, tx.b)) - min(tx.r, min(tx.g, tx.b));
    float gold = smoothstep(0.15, 0.45, tx.r - tx.b) * smoothstep(0.55, 0.95, tx.r + tx.g);
    float ruby = smoothstep(0.2, 0.45, tx.r - max(tx.g, tx.b) * 2.2) * smoothstep(0.15, 0.4, tx.r);
    float pale = smoothstep(0.45, 0.8, lum) * (1.0 - smoothstep(0.15, 0.4, sat));
    float dark = 1.0 - smoothstep(0.02, 0.09, lum);
    vec2 uv = vMapUv;
    float ct = vCloth.x, cv = vCloth.y;
    // distance (cloth fractions) to the nearest free edge: the top, the tapered bottom and the fly end (the hoist is on the pole)
    float edge = min(min(cv, 1.0 - cv) * 0.55, 1.0 - ct);
    // a light sweeping across the cloth, pole to fly, every so often (s: position along the sweep, period seconds)
    #define CL_SWEEP(s, period, width) (mo * exp(-pow(((s) - (fract(t / (period)) * 2.4 - 0.6)) * (width), 2.0)))
    int fx = int(uClothFx + 0.5);
    if (fx == 1) {                                                // Ashen: the edges smoulder, charred, a glowing line creeping
      float n = clNoise(vec2(ct * 9.0, cv * 6.0) + vec2(0.0, -t * 0.5 * mo));
      float e = edge + (n - 0.5) * 0.06;
      diffuseColor.rgb *= 1.0 - 0.8 * smoothstep(0.045, 0.0, e);
      float line = smoothstep(0.014, 0.0, abs(e - 0.035));
      totalEmissiveRadiance += vec3(1.0, 0.32, 0.05) * line * (1.0 + 0.7 * sin(t * 6.0 * mo + n * 14.0));
      totalEmissiveRadiance += vec3(1.0, 0.45, 0.1) * clTwinkle(uv * 22.0 + vec2(0.0, -t * 0.4), 0.18, 2.5, t) * de * 2.0 * (1.0 - gold);
    } else if (fx == 2) {                                         // Crowned: gold thread catches a sweep of light; the rubies glint
      totalEmissiveRadiance += vec3(1.0, 0.82, 0.4) * gold * CL_SWEEP(ct * 1.1 + cv * 0.5, 3.6, 10.0) * 1.1;
      totalEmissiveRadiance += vec3(1.0, 0.08, 0.1) * ruby * (0.25 + 1.2 * mo * pow(0.5 + 0.5 * sin(t * 2.3), 8.0));
    } else if (fx == 3) {                                         // Frostfall: frost rimes the edges, breathing in and out; ice glints
      float n = clNoise(uv * 16.0) * 0.6 + clNoise(uv * 41.0) * 0.4;
      float rime = smoothstep(0.07, 0.0, edge + (n - 0.5) * 0.07 - 0.015 * sin(t * 0.6 * mo));
      diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.78, 0.9, 1.0), rime * 0.85);
      totalEmissiveRadiance += vec3(0.75, 0.92, 1.0) * clTwinkle(uv * 26.0, 0.3, 3.0, t) * de * (rime + pale) * 2.2;
      totalEmissiveRadiance += vec3(0.55, 0.8, 1.0) * pale * CL_SWEEP(ct * 0.9 - cv * 0.4, 5.0, 5.0) * 0.6;
    } else if (fx == 4) {                                         // Greenwake: sunlight dapples through leaves; the blossom glows
      float d = smoothstep(0.55, 0.85, clNoise(uv * 3.5 + vec2(t * 0.12, t * 0.05) * mo));
      diffuseColor.rgb *= 1.0 + 0.45 * d;
      float pink = smoothstep(0.1, 0.3, min(tx.r - tx.g, tx.b - tx.g));
      totalEmissiveRadiance += vec3(1.0, 0.45, 0.65) * pink * (0.15 + 0.25 * mo * (0.5 + 0.5 * sin(t * 1.6)));
      totalEmissiveRadiance += vec3(1.0, 0.95, 0.75) * clTwinkle(uv * 18.0 + vec2(t * 0.2, 0.0), 0.1, 2.0, t) * de * d * 1.2;
    } else if (fx == 5) {                                         // Sunfire: flames lick along the free edges; the sun breathes
      float n1 = clNoise(vec2(cv * 8.0 + ct * 3.0, ct * 5.0 - t * 2.4 * mo));
      float n2 = clNoise(vec2(cv * 17.0, ct * 11.0 - t * 4.0 * mo));
      float e = edge + (n1 - 0.5) * 0.08 + (n2 - 0.5) * 0.035;
      float flame = smoothstep(0.06, 0.0, e);
      totalEmissiveRadiance += mix(vec3(1.0, 0.2, 0.02), vec3(1.0, 0.8, 0.3), smoothstep(0.025, 0.0, e)) * flame * (1.4 + 0.6 * n2);
      diffuseColor.rgb *= 1.0 - 0.5 * flame;
      float sun = gold * smoothstep(0.7, 0.95, tx.g + tx.r * 0.2);
      totalEmissiveRadiance += vec3(1.0, 0.62, 0.18) * (sun * (0.35 + 0.35 * mo * sin(t * 2.6)) + gold * CL_SWEEP(ct + cv * 0.3, 3.0, 6.0) * 1.2);
    } else if (fx == 6) {                                         // Harvest: a broad moonlit sheen drifts over; the moon glows; chaff glints
      totalEmissiveRadiance += vec3(1.0, 0.88, 0.62) * (0.04 + lum) * CL_SWEEP(ct * 0.7 - cv * 0.8, 6.5, 4.5) * 0.6;
      totalEmissiveRadiance += vec3(1.0, 0.94, 0.75) * pale * (0.25 + 0.2 * mo * sin(t * 1.2));
      totalEmissiveRadiance += vec3(1.0, 0.8, 0.4) * gold * clTwinkle(uv * 30.0, 0.25, 2.2, t) * de * 2.0;
    } else if (fx == 7) {                                         // Champion's Laurel: gold glitter twinkles over the leaves; a slow gleam
      totalEmissiveRadiance += vec3(1.0, 0.86, 0.45) * gold * (clTwinkle(uv * 44.0, 0.4, 3.5, t) * de * 3.0 + CL_SWEEP(ct + cv * 0.6, 5.0, 6.0) * 1.2);
    } else if (fx == 8) {                                         // Iron Crown: a cold steel glint; the rubies beat; forge heat from below
      float steel = smoothstep(0.25, 0.55, lum) * (1.0 - smoothstep(0.1, 0.3, sat));
      totalEmissiveRadiance += vec3(0.7, 0.85, 1.0) * steel * CL_SWEEP(ct * 1.2 - cv * 0.3, 3.2, 9.0) * 2.0;
      float beat = pow(max(0.0, sin(t * 2.0)), 12.0) + 0.6 * pow(max(0.0, sin(t * 2.0 - 0.5)), 12.0);
      totalEmissiveRadiance += vec3(1.0, 0.06, 0.08) * ruby * (0.3 + 1.4 * mo * beat);
      totalEmissiveRadiance += vec3(0.9, 0.18, 0.04) * smoothstep(0.16, 0.0, cv) * (0.18 + 0.12 * mo * sin(t * 1.7 + ct * 4.0));
    } else if (fx == 9) {                                         // Racer's Hourglass: gold sand glitters as it falls
      totalEmissiveRadiance += vec3(1.0, 0.82, 0.45) * gold * clTwinkle(vec2(uv.x * 34.0, uv.y * 34.0 + t * 3.0 * mo), 0.35, 4.0, t) * de * 2.6;
      totalEmissiveRadiance += vec3(1.0, 0.85, 0.5) * gold * CL_SWEEP(cv * -1.0 + 1.0, 4.0, 6.0) * 1.0;
    } else if (fx == 10) {                                        // Wayfarer's banner: stars twinkle on the night; the compass turns its light
      totalEmissiveRadiance += vec3(0.75, 1.0, 0.95) * clTwinkle(uv * 20.0, 0.22, 1.6, t) * de * (0.4 + dark) * 1.8;
      vec2 c = uv - 0.5; float a = atan(c.y, c.x);
      totalEmissiveRadiance += vec3(1.0, 0.9, 0.6) * gold * pow(max(0.0, cos(a * 4.0 - t * 1.2 * mo)), 16.0) * mo * 1.6 * smoothstep(0.42, 0.1, length(c));
    }
  }`;
